import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { useConfirm } from '@/components/common/ConfirmDialog'
import {
  applyClientSideTableState,
  DataTable,
  useTableState,
  type ColumnDef,
} from '@/components/data-table'
import { BoolIcon } from '@/components/common/BoolIcon'
import { AddButton } from '@/components/common/AddButton'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { useDeleteRole, useRoles } from './hooks'
import { RoleFormDialog } from './components/RoleFormDialog'
import { formatError } from './components/formatError'
import type { Role } from './types'

const TABLE_ID = 'admin-roles'

export function RolesAdminPage() {
  const confirm = useConfirm()
  const rolesQ = useRoles()
  const del = useDeleteRole()
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Role | null>(null)
  const { state, setSort, setSearch, setFilter, removeFilter, clearFilters } = useTableState(TABLE_ID)

  const columns: ColumnDef<Role>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Nazwa',
        sortable: true,
        filter: { kind: 'text', placeholder: 'Filtruj nazwę...' },
        accessor: (r) => (
          <Link to={`/admin/roles/${r.code}`} className="text-sky-700 hover:underline">
            {r.name}
          </Link>
        ),
        alwaysVisible: true,
      },
      {
        key: 'code',
        header: 'Kod',
        sortable: true,
        filter: { kind: 'text', placeholder: 'Filtruj kod...' },
        accessor: (r) => <span className="font-mono text-xs">{r.code}</span>,
        className: 'whitespace-nowrap',
      },
      {
        key: 'description',
        header: 'Opis',
        accessor: (r) => <span className="text-slate-600">{r.description}</span>,
      },
      {
        key: 'isActive',
        header: 'Aktywna',
        sortable: true,
        filter: { kind: 'bool', trueLabel: 'Aktywne', falseLabel: 'Nieaktywne' },
        accessor: (r) => <BoolIcon value={r.isActive} />,
        className: 'w-24',
      },
      {
        key: 'actions',
        header: '',
        accessor: (r) => (
          <div className="text-right whitespace-nowrap">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setEditing(r)
              }}
              className="text-xs text-slate-600 hover:text-sky-700 px-1"
            >
              Edytuj
            </button>
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation()
                const ok = await confirm({
                  title: `Usunąć rolę "${r.name}"?`,
                  description: 'Skasowane zostaną też wszystkie jej reguły widzialności i przypisania użytkowników.',
                  confirmLabel: 'Usuń',
                  variant: 'destructive',
                })
                if (ok) del.mutate(r.code, { onError: (err) => toast.error(formatError(err)) })
              }}
              className="text-xs text-red-600 px-1 ml-1"
            >
              Usuń
            </button>
          </div>
        ),
        className: 'w-32',
        alwaysVisible: true,
      },
    ],
    [del, confirm],
  )

  const filteredRows = useMemo(
    () =>
      applyClientSideTableState(rolesQ.data ?? [], columns, state, {
        valueGetters: {
          name: (r) => r.name,
          code: (r) => r.code,
          description: (r) => r.description,
          isActive: (r) => r.isActive,
        },
        searchString: (r) => `${r.name} ${r.code} ${r.description ?? ''}`.toLowerCase(),
      }),
    [rolesQ.data, columns, state],
  )

  return (
    <div className="flex flex-col w-full h-full">
      <ListPageHeader
        title="Role i widzialność"
        subtitle={`Każda rola ma uprawnienia ("co może") i reguły widzialności ("co widzi").`}
        actions={<AddButton onClick={() => setCreating(true)}>Dodaj rolę</AddButton>}
      />

      <div className="p-4">
        <DataTable<Role>
          tableId={TABLE_ID}
          data={filteredRows}
          columns={columns}
          rowKey={(r) => r.id}
          isLoading={rolesQ.isLoading}
          isError={rolesQ.isError}
          sort={state.sort}
          onSortChange={setSort}
          state={state}
          searchable
          searchPlaceholder="Szukaj nazwy / kodu / opisu..."
          onSearchChange={setSearch}
          onFilterChange={setFilter}
          onFilterRemove={removeFilter}
          onClearAll={clearFilters}
          emptyState="Brak ról. Dodaj pierwszą."
        />
      </div>

      {(creating || editing) && (
        <RoleFormDialog
          editing={editing}
          onClose={() => {
            setCreating(false)
            setEditing(null)
          }}
        />
      )}
    </div>
  )
}
